import { useCallback, useState } from 'react';
import {
  Button,
  FileDropzone,
  IconClose,
  IconFile,
  formatBytes,
  useToast,
} from '@/design-system';
import { mediaApi, toUserMessage } from '@/lib/api';

/**
 * Evidências da não-conformidade — upload pelo MEDIA, igual ao GED.
 *
 * O binário NUNCA passa pelo CORE: `mediaApi.uploadFile` faz initiate → PUT
 * pré-assinado → finalize, e o CORE recebe apenas o `uploadId` de um objeto já
 * verificado (tamanho, SHA-256, magic bytes, antivírus). O que a NC guarda é a
 * REFERÊNCIA.
 *
 * PARIDADE CORRIGIDA sgq.md §A.6: no legado o campo era um `<input type=file>`
 * único que ia para o Google Drive com `mimeType: application/octet-stream`, e
 * a FALHA DE UPLOAD NÃO BLOQUEAVA a criação da NC — a NC nascia sem a evidência
 * e o usuário via só um aviso de sessão. Aqui o envio acontece ANTES do
 * comando: o `uploadId` só existe se o arquivo chegou íntegro, e um envio que
 * falha é erro visível, não aviso.
 *
 * ACESSIBILIDADE: `<FileDropzone>` já garante que arrastar não é o único
 * caminho (WCAG 2.5.7) e expõe o progresso em `role="progressbar"`. A lista de
 * enviados é um `<ul>` rotulado, e cada remoção anuncia o resultado.
 */

export interface EvidenceReference {
  readonly uploadId: string;
  readonly fileName: string;
  readonly sizeBytes: number;
  readonly sha256: string;
}

export interface EvidenceUploaderProps {
  readonly value: readonly EvidenceReference[];
  readonly onChange: (value: readonly EvidenceReference[]) => void;
  readonly disabled?: boolean;
  /** Limite do schema `createNonconformitySchema.evidenceUploadIds` (20). */
  readonly maxFiles?: number;
  readonly className?: string;
}

export function EvidenceUploader({
  value,
  onChange,
  disabled = false,
  maxFiles = 20,
  className,
}: EvidenceUploaderProps) {
  const { toast } = useToast();
  const [pending, setPending] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const full = value.length >= maxFiles;

  const upload = useCallback(
    async (file: File) => {
      setPending(file);
      setProgress(0);
      try {
        const result = await mediaApi.uploadFile({ file, onProgress: setProgress });
        onChange([
          ...value,
          {
            uploadId: result.uploadId,
            fileName: result.fileName,
            sizeBytes: file.size,
            sha256: result.sha256,
          },
        ]);
        setAnnouncement(`Evidência ${result.fileName} enviada. ${value.length + 1} de ${maxFiles}.`);
      } catch (error) {
        toast({
          tone: 'error',
          title: 'Não foi possível enviar a evidência',
          description: toUserMessage(error),
        });
        setAnnouncement('Falha ao enviar a evidência. Nenhum arquivo foi anexado.');
      } finally {
        setPending(null);
        setProgress(null);
      }
    },
    [maxFiles, onChange, toast, value],
  );

  return (
    <div className={className}>
      <FileDropzone
        file={pending}
        progress={progress}
        disabled={disabled || full || progress !== null}
        label="Evidência da não-conformidade"
        hint={
          full
            ? `Limite de ${maxFiles} evidências atingido. Remova uma para enviar outra.`
            : `Fotos, laudos, relatórios ou registros que comprovem o fato. Até ${maxFiles} arquivos.`
        }
        onFileChange={(file) => {
          if (!file) {
            setPending(null);
            return;
          }
          void upload(file);
        }}
      />

      {value.length > 0 ? (
        <ul
          aria-label={`Evidências anexadas (${value.length} de ${maxFiles})`}
          className="mt-3 flex flex-col gap-1.5"
        >
          {value.map((evidence) => (
            <li
              key={evidence.uploadId}
              className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2"
            >
              <IconFile size={16} aria-hidden="true" className="shrink-0 text-content-subtle" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-content">{evidence.fileName}</p>
                <p className="text-xs text-content-subtle qc-tabular">
                  {formatBytes(evidence.sizeBytes)} · SHA-256 {evidence.sha256.slice(0, 8)}…
                </p>
              </div>
              <Button
                size="icon"
                variant="ghost"
                disabled={disabled}
                iconLabel={`Remover a evidência ${evidence.fileName}`}
                onClick={() => {
                  onChange(value.filter((item) => item.uploadId !== evidence.uploadId));
                  setAnnouncement(`Evidência ${evidence.fileName} removida.`);
                }}
              >
                <IconClose size={15} />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      <span aria-live="polite" className="qc-sr-only">
        {announcement}
      </span>
    </div>
  );
}
